import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Heading2,
    TextDefault,
    ThemedButton,
    SpaceBetweenDiv,
} from '@awarego-ui/components';
import { Alert } from '@mui/material';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';

function CompanyMicrosoftDefenderAstIntegration({ companyId }) {
    const { commonStore } = useStores();
    const [searchParams, setSearchParams] = useSearchParams();
    const svc = services.Companies.microsoftDefenderAstService(companyId);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const st = await svc.status();
            setStatus(st);
        } catch (e) {
            commonStore.showMessage(e.message || String(e), 'error');
        } finally {
            setLoading(false);
        }
    }, [svc, commonStore]);

    useEffect(() => {
        const message = searchParams.get('message');
        const messageType = searchParams.get('messageType');
        if (message) {
            commonStore.showMessage(
                message,
                messageType === 'error' ? 'error' : 'success'
            );
            searchParams.delete('message');
            searchParams.delete('messageType');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, commonStore]);

    useEffect(() => {
        load();
    }, [load]);

    const onDisconnect = async () => {
        try {
            await svc.disconnect();
            commonStore.showMessage('Microsoft 365 connection removed.', 'success');
            await load();
        } catch (e) {
            commonStore.showMessage(e.message || String(e), 'error');
        }
    };

    if (loading && !status) {
        return <ThemedSpinner />;
    }

    const connected = status && status.connected;
    const consentUrl = `${import.meta.env.REACT_APP_API_URL}/microsoft-defender-ast/authorize/${companyId}`;

    return (
        <div className="top-guide">
            <div className="block new-settings-block">
                <SpaceBetweenDiv>
                    <Heading2 displayBlock>Microsoft Defender simulations</Heading2>
                    <Link to="../integrations">
                        <ThemedButton size="small" mode="secondary" text="Back" />
                    </Link>
                </SpaceBetweenDiv>
                <TextDefault lighter>
                    Link your organization&apos;s Microsoft 365 tenant so AwareGO can
                    read and manage Attack Simulation Training (Defender Plan 2) in
                    line with your settings.
                </TextDefault>
                <TextDefault lighter style={{ marginTop: 12 }}>
                    After you connect, open the{' '}
                    <strong>Phishing simulation</strong> tab in the main menu for
                    campaigns, templates, and reports.
                </TextDefault>
                <br />
                {!connected ? (
                    <div>
                        <TextDefault>
                            A Microsoft 365 administrator must approve access once for
                            your organization.
                        </TextDefault>
                        <br />
                        <br />
                        <a href={consentUrl} target="_blank" rel="noreferrer">
                            <ThemedButton
                                mode="primary"
                                text="Connect Microsoft 365"
                            />
                        </a>
                    </div>
                ) : (
                    <div style={{ marginTop: 16 }}>
                        <TextDefault>
                            <strong>Connected.</strong> Your Microsoft tenant ID is{' '}
                            <code>
                                {status.connection &&
                                    status.connection.aad_tenant_id}
                            </code>
                            .
                        </TextDefault>
                        {status.licenseHint &&
                            status.licenseHint.checked &&
                            !status.licenseHint.hasDefenderForOffice365Plan2 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    We did not detect{' '}
                                    <strong>
                                        Microsoft Defender for Office 365 Plan 2
                                    </strong>{' '}
                                    on this tenant. Attack Simulation Training
                                    requires that capability.{' '}
                                    <a
                                        href={status.licenseHint.learnMoreUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Get started
                                    </a>
                                    {' · '}
                                    <a
                                        href={status.licenseHint.acquireUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Plans and add-ons
                                    </a>
                                    {' · '}
                                    <a
                                        href={status.licenseHint.trialUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Try Defender for Office 365
                                    </a>
                                    .
                                </Alert>
                            )}
                        {status.licenseHint && !status.licenseHint.checked && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                We could not verify Microsoft 365 licensing
                                automatically (the Entra app may be missing{' '}
                                <code>Organization.Read.All</code>). If simulations
                                fail to load, confirm Plan 2 is assigned.{' '}
                                <a
                                    href={status.licenseHint.learnMoreUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Get started
                                </a>
                                .
                            </Alert>
                        )}
                        {status.health && !status.health.ok && (
                            <TextDefault style={{ color: '#c62828', marginTop: 8 }}>
                                Microsoft could not be reached:{' '}
                                {status.health.error}
                            </TextDefault>
                        )}
                        <br />
                        <br />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <Link to="/phishing-simulation">
                                <ThemedButton
                                    mode="primary"
                                    text="Open phishing simulation"
                                />
                            </Link>
                            <ThemedButton
                                mode="secondary"
                                text="Disconnect"
                                onClick={onDisconnect}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default observer(CompanyMicrosoftDefenderAstIntegration);
